import React, { useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import { cssMap, cx } from '@atlaskit/css';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { token } from '@atlaskit/tokens';
import { Anchor, Box, Flex, Stack, Text } from '@atlaskit/primitives/compiled';
import Pressable from '@atlaskit/primitives/pressable';
import { ModalHeader, ModalBody } from '@atlaskit/modal-dialog';
import CheckCircleIcon from '@atlaskit/icon/core/check-circle';
import CheckCircleUncheckedIcon from '@atlaskit/icon/core/check-circle-unchecked';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import DefaultAppIcon from '@atlaskit/icon/core/app';

import Spinner from '@atlaskit/spinner';
import { FallbackableImage } from '@confluence/fallbackable-image';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

import { useContentTitles } from './hooks/useContentTitles.ts';
import {
	type Suggestion,
	type SuggestionGroup,
	PRODUCT_ICON_MAP,
	DEFAULT_PRODUCT_INFO,
} from './types/RemoteObject.ts';
import { StackedContentTypeIcons } from './StackedContentTypeIcons.tsx';

type ParentGrouping = {
	parentTitle: string;
	suggestions: Suggestion[];
};

type ReviewContentSuggestionsModalProps = {
	groups: SuggestionGroup[];
	contentTypes: string[];
	isLoadingSuggestions?: boolean;
	onClose: () => void;
	onSaveSuccess: () => void;
};

type SuggestionItemRowProps = {
	suggestion: Suggestion;
	isSelected: boolean;
	onToggleSelectItem: (id: string) => void;
};

type SuggestionGroupRowProps = {
	parentTitle: string;
	suggestions: Suggestion[];
	isExpanded: boolean;
	isGroupSelected: boolean;
	selectedItems: Set<string>;
	toggleExpand: (parentId: number) => void;
	parentId: number;
	toggleSelectGroup: (groupSuggestions: Suggestion[]) => void;
	onToggleSelectItem: (id: string) => void;
};

type ModalLeftPanelProps = {
	groupedSuggestionsWithTitles: Map<number, ParentGrouping>;
	selectedItems: Set<string>;
	expandedGroups: Set<number>;
	toggleExpand: (parentId: number) => void;
	toggleSelectGroup: (groupSuggestions: Suggestion[]) => void;
	toggleSelectItem: (itemId: string) => void;
};

const i18n = defineMessages({
	bringTogetherTitle: {
		id: 'side-navigation.review-content-suggestions-modal.bring-together-title',
		defaultMessage: 'Bring together the info that matters',
		description: 'Main title on the left panel of the content suggestions modal',
	},
	bringTogetherDescription: {
		id: 'side-navigation.review-content-suggestions-modal.bring-together-description',
		defaultMessage: 'Use these suggestions to organize everything in one space',
		description: 'Description on the left panel of the content suggestions modal',
	},
	addItemsToSpace: {
		id: 'side-navigation.review-content-suggestions-modal.add-items-to-space',
		defaultMessage: 'Add {count, plural, one {# item} other {# items}} to space',
		description: 'Button label for adding selected content suggestions to space',
	},
	expandGroup: {
		id: 'side-navigation.review-content-suggestions-modal.expand-group',
		defaultMessage: 'Expand {title}',
		description: 'Accessible label for expanding a suggestion group',
	},
	collapseGroup: {
		id: 'side-navigation.review-content-suggestions-modal.collapse-group',
		defaultMessage: 'Collapse {title}',
		description: 'Accessible label for collapsing a suggestion group',
	},
	openSuggestion: {
		id: 'side-navigation.review-content-suggestions-modal.open-suggestion',
		defaultMessage: 'Open {title} in a new tab',
		description: 'Accessible label for opening a suggestion link in a new tab',
	},
	deselectGroup: {
		id: 'side-navigation.review-content-suggestions-modal.deselect-group',
		defaultMessage: 'Deselect group {title}',
		description: 'Accessible label for deselecting all items in a suggestion group',
	},
	selectGroup: {
		id: 'side-navigation.review-content-suggestions-modal.select-group',
		defaultMessage: 'Select group {title}',
		description: 'Accessible label for selecting all items in a suggestion group',
	},
	deselectItem: {
		id: 'side-navigation.review-content-suggestions-modal.deselect-item',
		defaultMessage: 'Deselect {title}',
		description: 'Accessible label for deselecting a single suggestion item',
	},
	selectItem: {
		id: 'side-navigation.review-content-suggestions-modal.select-item',
		defaultMessage: 'Select {title}',
		description: 'Accessible label for selecting a single suggestion item',
	},
	untitled: {
		id: 'side-navigation.review-content-suggestions-modal.untitled',
		defaultMessage: 'Untitled',
		description: 'Fallback when parent content has no title',
	},
	loading: {
		id: 'side-navigation.review-content-suggestions-modal.loading',
		defaultMessage: 'Loading suggestions…',
		description: 'Accessible label for the loading state in the content suggestions modal',
	},
});

function getSuggestionIconUrl(url: string): string {
	for (const entry of PRODUCT_ICON_MAP) {
		if (url.includes(entry.pattern)) {
			return entry.iconUrl;
		}
	}
	return DEFAULT_PRODUCT_INFO.iconUrl;
}

const styles = cssMap({
	layout: {
		height: '560px',
	},
	leftPanel: {
		flexGrow: 1,
		flexBasis: '0%',
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.300'),
		width: '50%',
	},
	groupListScrollArea: {
		flexGrow: 1,
		overflowY: 'auto',
		scrollbarWidth: 'none',
		minHeight: '0%',
	},
	rightPanel: {
		flexGrow: 1,
		flexBasis: '0%',
		backgroundColor: token('color.background.discovery'),
		borderRadius: token('radius.medium'),
		width: '50%',
	},
	groupCard: {
		borderRadius: token('radius.medium'),
		overflow: 'hidden',
		backgroundColor: token('color.background.neutral'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		'&:hover': {
			borderColor: token('color.border.bold'),
		},
		transition: 'border-color 150ms ease',
	},
	groupCardExpanded: {
		borderColor: token('color.border.bold'),
	},
	checkButton: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		paddingBlock: token('space.150'),
		paddingInlineStart: token('space.150'),
		paddingInlineEnd: token('space.100'),
		backgroundColor: 'transparent',
		flexShrink: 0,
		'&:hover': {
			// @ts-expect-error - AllowedStyles expects token values; transparent needed to inherit parent bg
			backgroundColor: 'transparent',
		},
	},
	expandButton: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-start',
		textAlign: 'start',
		gap: token('space.100'),
		flexGrow: 1,
		paddingBlock: token('space.150'),
		paddingInlineStart: token('space.050'),
		paddingInlineEnd: token('space.150'),
		backgroundColor: 'transparent',
		'&:hover': {
			// @ts-expect-error - AllowedStyles expects token values; transparent needed to inherit parent bg
			backgroundColor: 'transparent',
		},
	},
	groupTitleWrapper: {
		flexGrow: 1,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	itemsListOuter: {
		display: 'grid',
		gridTemplateRows: '0fr',
		opacity: 0,
		transition: 'grid-template-rows 350ms ease, opacity 250ms ease',
	},
	itemsListOuterExpanded: {
		gridTemplateRows: '1fr',
		opacity: 1,
	},
	itemsList: {
		overflow: 'hidden',
		minHeight: '0%',
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
	},
	itemCheckButton: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		paddingBlock: token('space.100'),
		paddingInlineStart: token('space.150'),
		paddingInlineEnd: token('space.100'),
		backgroundColor: 'transparent',
		flexShrink: 0,
		'&:hover': {
			// @ts-expect-error - AllowedStyles expects token values; transparent needed to inherit parent bg
			backgroundColor: 'transparent',
		},
	},
	suggestionLinkWrapper: {
		flexGrow: 1,
		minWidth: '0%',
	},
	suggestionLink: {
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'nowrap',
		gap: token('space.100'),
		minWidth: '0%',
		paddingBlock: token('space.075'),
		paddingInlineEnd: token('space.150'),
		textDecoration: 'none',
		color: token('color.text'),
		'&:hover': {
			textDecoration: 'none',
		},
	},
	suggestionIconSlot: {
		width: '16px',
		height: '16px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
	},
	suggestionTitleWrapper: {
		flexGrow: 1,
		minWidth: '0%',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	suggestionLinkExternalIcon: {
		flexShrink: 0,
	},
	addButtonRow: {
		paddingBlockEnd: token('space.200'),
	},
	itemRow: {
		'&:hover': {
			backgroundColor: token('color.background.neutral.hovered'),
		},
	},
	loadingOverlay: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		minHeight: '560px',
	},
});

const SuggestionItemRow = ({
	suggestion,
	isSelected,
	onToggleSelectItem,
}: SuggestionItemRowProps) => {
	const { formatMessage } = useIntl();
	const iconUrl = getSuggestionIconUrl(suggestion.url);

	return (
		<Flex alignItems="center" xcss={styles.itemRow}>
			<Pressable
				xcss={styles.itemCheckButton}
				onClick={() => onToggleSelectItem(suggestion.id)}
				aria-label={formatMessage(isSelected ? i18n.deselectItem : i18n.selectItem, {
					title: suggestion.title,
				})}
			>
				{isSelected ? (
					<CheckCircleIcon label="" color={token('color.icon')} />
				) : (
					<CheckCircleUncheckedIcon label="" color={token('color.icon.subtle')} />
				)}
			</Pressable>
			<Box xcss={styles.suggestionLinkWrapper}>
				<Anchor
					href={suggestion.url}
					target="_blank"
					rel="noopener noreferrer"
					aria-label={formatMessage(i18n.openSuggestion, { title: suggestion.title })}
					xcss={styles.suggestionLink}
				>
					<Box xcss={styles.suggestionIconSlot} aria-hidden="true">
						{iconUrl ? (
							<FallbackableImage
								src={iconUrl}
								width="16"
								height="16"
								fallback={() => <DefaultAppIcon label="" />}
								role="presentation"
							/>
						) : (
							<DefaultAppIcon label="" color={token('color.icon.subtle')} />
						)}
					</Box>
					<Box xcss={styles.suggestionTitleWrapper}>
						<Text>{suggestion.title}</Text>
					</Box>
					<Box xcss={styles.suggestionLinkExternalIcon} aria-hidden="true">
						<LinkExternalIcon label="" color={token('color.icon.subtle')} />
					</Box>
				</Anchor>
			</Box>
		</Flex>
	);
};

const SuggestionGroupRow = ({
	parentTitle,
	suggestions,
	isExpanded,
	isGroupSelected,
	selectedItems,
	toggleExpand,
	parentId,
	toggleSelectGroup,
	onToggleSelectItem,
}: SuggestionGroupRowProps) => {
	const { formatMessage } = useIntl();

	return (
		<Box xcss={cx(styles.groupCard, isExpanded && styles.groupCardExpanded)}>
			<Flex>
				<Pressable
					xcss={styles.checkButton}
					onClick={() => toggleSelectGroup(suggestions)}
					aria-label={formatMessage(isGroupSelected ? i18n.deselectGroup : i18n.selectGroup, {
						title: parentTitle,
					})}
				>
					{isGroupSelected ? (
						<CheckCircleIcon label="" color={token('color.icon')} />
					) : (
						<CheckCircleUncheckedIcon label="" color={token('color.icon.subtle')} />
					)}
				</Pressable>
				<Pressable
					xcss={styles.expandButton}
					onClick={() => toggleExpand(parentId)}
					aria-expanded={isExpanded}
					aria-label={formatMessage(isExpanded ? i18n.collapseGroup : i18n.expandGroup, {
						title: parentTitle,
					})}
				>
					<Box xcss={styles.groupTitleWrapper}>
						<Text weight="medium">{parentTitle}</Text>
					</Box>
					<Text color="color.text.subtle">{suggestions.length}</Text>
					{isExpanded ? (
						<ChevronDownIcon label="" color={token('color.icon.subtle')} />
					) : (
						<ChevronRightIcon label="" color={token('color.icon.subtle')} />
					)}
				</Pressable>
			</Flex>
			<Box
				xcss={cx(styles.itemsListOuter, isExpanded && styles.itemsListOuterExpanded)}
				aria-hidden={!isExpanded}
			>
				<Box xcss={styles.itemsList}>
					{suggestions.map((suggestion) => (
						<SuggestionItemRow
							key={suggestion.id}
							suggestion={suggestion}
							isSelected={selectedItems.has(suggestion.id)}
							onToggleSelectItem={onToggleSelectItem}
						/>
					))}
				</Box>
			</Box>
		</Box>
	);
};

const ModalLeftPanel = ({
	groupedSuggestionsWithTitles,
	selectedItems,
	expandedGroups,
	toggleExpand,
	toggleSelectGroup,
	toggleSelectItem,
}: ModalLeftPanelProps) => {
	const { formatMessage } = useIntl();

	return (
		<Stack space="space.100">
			{[...groupedSuggestionsWithTitles.entries()].map(
				([parentId, { parentTitle, suggestions: groupSuggestions }]) => {
					const isGroupSelected = groupSuggestions.every((s) => selectedItems.has(s.id));
					return (
						<SuggestionGroupRow
							key={parentId}
							parentTitle={parentTitle || formatMessage(i18n.untitled)}
							suggestions={groupSuggestions}
							isExpanded={expandedGroups.has(parentId)}
							isGroupSelected={isGroupSelected}
							selectedItems={selectedItems}
							toggleExpand={toggleExpand}
							parentId={parentId}
							toggleSelectGroup={toggleSelectGroup}
							onToggleSelectItem={toggleSelectItem}
						/>
					);
				},
			)}
		</Stack>
	);
};

export const ReviewContentSuggestionsModal = ({
	groups,
	contentTypes,
	isLoadingSuggestions = false,
	onClose,
	onSaveSuccess,
}: ReviewContentSuggestionsModalProps) => {
	const { formatMessage } = useIntl();
	const { Modal } = useChoreographerAssets();

	const allSuggestions = useMemo(() => groups.flatMap((g) => g.remoteObjects), [groups]);

	const [expandedGroups, setExpandedGroups] = useState<Set<number>>(() => new Set());
	const [selectedItems, setSelectedItems] = useState<Set<string>>(
		() => new Set(allSuggestions.map((s) => s.id)),
	);

	const parentIds = useMemo(() => groups.map((g) => String(g.parentId)), [groups]);

	const { titlesByContentId, isLoading: isLoadingTitles } = useContentTitles(parentIds);

	const isLoading = isLoadingSuggestions || isLoadingTitles;

	const groupedSuggestionsWithTitles = useMemo(() => {
		const result = new Map<number, ParentGrouping>();
		groups.forEach((group) => {
			result.set(group.parentId, {
				parentTitle: titlesByContentId.get(String(group.parentId)) ?? '',
				suggestions: group.remoteObjects,
			});
		});
		return result;
	}, [groups, titlesByContentId]);

	const toggleExpand = (parentId: number) => {
		setExpandedGroups((prev) => {
			const next = new Set(prev);
			if (next.has(parentId)) {
				next.delete(parentId);
			} else {
				next.add(parentId);
			}
			return next;
		});
	};

	const toggleSelectItem = (itemId: string) => {
		setSelectedItems((prev) => {
			const next = new Set(prev);
			if (next.has(itemId)) {
				next.delete(itemId);
			} else {
				next.add(itemId);
			}
			return next;
		});
	};

	const toggleSelectGroup = (groupSuggestions: Suggestion[]) => {
		const allSelected = groupSuggestions.every((s) => selectedItems.has(s.id));
		setSelectedItems((prev) => {
			const next = new Set(prev);
			groupSuggestions.forEach((s) => {
				if (allSelected) {
					next.delete(s.id);
				} else {
					next.add(s.id);
				}
			});
			return next;
		});
	};

	return (
		<Modal onClose={onClose} width="x-large">
			<ModalHeader hasCloseButton />
			<ModalBody>
				{isLoading ? (
					<Box xcss={styles.loadingOverlay} testId="review-content-suggestions-modal-loading">
						<Spinner size="large" label={formatMessage(i18n.loading)} />
					</Box>
				) : (
					<Flex xcss={styles.layout}>
						<Box xcss={styles.leftPanel}>
							<Stack space="space.100">
								<StackedContentTypeIcons contentTypes={contentTypes} variant="large" />
								<Heading size="medium">{formatMessage(i18n.bringTogetherTitle)}</Heading>
								<Text color="color.text.subtle">
									{formatMessage(i18n.bringTogetherDescription)}
								</Text>
							</Stack>
							<Box xcss={styles.groupListScrollArea}>
								<ModalLeftPanel
									groupedSuggestionsWithTitles={groupedSuggestionsWithTitles}
									selectedItems={selectedItems}
									expandedGroups={expandedGroups}
									toggleExpand={toggleExpand}
									toggleSelectGroup={toggleSelectGroup}
									toggleSelectItem={toggleSelectItem}
								/>
							</Box>
							<Flex justifyContent="end" xcss={styles.addButtonRow}>
								<Button
									appearance="primary"
									onClick={onSaveSuccess}
									isDisabled={selectedItems.size === 0}
								>
									{formatMessage(i18n.addItemsToSpace, { count: selectedItems.size })}
								</Button>
							</Flex>
						</Box>
						<Box xcss={styles.rightPanel} />
					</Flex>
				)}
			</ModalBody>
		</Modal>
	);
};
